import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

import { COUNTRY_NAMES } from './countries';
import type { PhoneCountry } from './countries';

/**
 * Get a human-readable country name from ISO2 code
 */
function getCountryName(iso2: string): string {
  return COUNTRY_NAMES[iso2] || iso2;
}

export interface PhoneMeta {
  countryIso2: string;
  callingCode: string;
  national: string;
  isValid: boolean;
}

const EMPTY_PHONE_RESULT = { country: null, national: '', isValid: false } as const

/**
 * Parse E.164 phone number and extract components
 */
export function parseE164(
  e164: string | null | undefined,
  countries: PhoneCountry[]
): { country: PhoneCountry | null; national: string; isValid: boolean } {
  if (!e164 || !e164.startsWith('+')) {
    return { ...EMPTY_PHONE_RESULT }
  }

  try {
    const parsed = parsePhoneNumber(e164);
    const callingCode = parsed.countryCallingCode.toString();
    let country = countries.find((c) => c.callingCode === callingCode);
    
    // If country not found in our list, create a temporary one from libphonenumber data
    if (!country && parsed.country) {
      // Get national number length from libphonenumber
      const nationalNumber = parsed.nationalNumber;
      const nationalDigits = nationalNumber.length;
      
      // Create a simple mask based on number length
      const mask = '0'.repeat(nationalDigits).match(/.{1,3}/g)?.join(' ') || '0'.repeat(nationalDigits);
      
      country = {
        iso2: parsed.country,
        name: getCountryName(parsed.country),
        callingCode: callingCode,
        mask: mask,
        nationalDigits: nationalDigits,
      };
    }
    
    const national = parsed.nationalNumber;
    const isValid = isValidPhoneNumber(e164);

    return {
      country: country || null,
      national,
      isValid,
    };
  } catch {
    return { ...EMPTY_PHONE_RESULT }
  }
}

/**
 * Build E.164 format from country and national number
 */
export function buildE164(country: PhoneCountry, national: string): string | null {
  if (!national || national.length === 0) {
    return null;
  }

  // Only return E.164 if we have the expected number of digits
  if (national.length !== country.nationalDigits) {
    return null;
  }

  return `+${country.callingCode}${national}`;
}

/**
 * Validate phone number using libphonenumber-js
 */
export function validatePhone(e164: string | null | undefined): boolean {
  if (!e164) return false;
  try {
    return isValidPhoneNumber(e164);
  } catch {
    return false;
  }
}
